import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { getLocalDateString } from '../utils/dateUtils';

const AdminScheduleViolations = () => {
  const { user } = useAuth();
  const [allUsersViolations, setAllUsersViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ mandatoryBreaks: false });
  const [error, setError] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all'); // all, current, future, past
  const [selectedUser, setSelectedUser] = useState('all');
  const [sortBy, setSortBy] = useState('user'); // user, date, severity

  // Env-driven limit for max available days per stick (defaults to 5)
  const STICK_MAX_AVAILABLE_DAYS = parseInt(process.env.REACT_APP_STICK_MAX_AVAILABLE_DAYS || '5', 10);

  // Fetch user settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setSettings({ mandatoryBreaks: false });
    }
  }, []);

  // Fetch all users' schedule violations
  const fetchAllUsersViolations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.get('/api/admin/schedule-violations');
      setAllUsersViolations(response.data);
      
    } catch (error) {
      console.error('Failed to fetch all users violations:', error);
      setError('Failed to load violations data. You may not have permission to view this page.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Note: All violation analysis is handled by the backend API
  // The frontend only needs to display and filter the pre-calculated violations

  useEffect(() => {
    if (user) {
      fetchSettings();
      fetchAllUsersViolations();
    }
  }, [user, fetchSettings, fetchAllUsersViolations]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateRange = (dates) => {
    if (dates.length === 1) {
      return formatDate(dates[0]);
    }
    return `${formatDate(dates[0])} - ${formatDate(dates[dates.length - 1])}`;
  };

  const getViolationIcon = (type) => {
    switch (type) {
      case 'stick_days':
        return '📅';
      case 'consecutive_days':
        return '🔗';
      default:
        return '⚠️';
    }
  };

  const getViolationColor = (severity) => {
    switch (severity) {
      case 'error':
        return '#dc2626'; // red-600
      case 'warning':
        return '#d97706'; // amber-600
      default:
        return '#6b7280'; // gray-500
    }
  };

  const isPastViolation = (dates) => {
    const today = new Date();
    const violationEndDate = new Date(dates[dates.length - 1]);
    return violationEndDate < today;
  };

  const isFutureViolation = (dates) => {
    const today = new Date();
    const violationStartDate = new Date(dates[0]);
    return violationStartDate > today;
  };

  const getCurrentViolation = (dates) => {
    const today = new Date();
    const todayStr = getLocalDateString(today);
    return dates.includes(todayStr);
  };

  // Filter violations based on selected filters
  const getFilteredViolations = () => {
    let filtered = [...allUsersViolations];

    // Filter by time period
    if (selectedFilter !== 'all') {
      filtered = filtered.map(userViolations => ({
        ...userViolations,
        violations: userViolations.violations.filter(violation => {
          if (selectedFilter === 'current') return getCurrentViolation(violation.dates);
          if (selectedFilter === 'future') return isFutureViolation(violation.dates);
          if (selectedFilter === 'past') return isPastViolation(violation.dates);
          return true;
        })
      })).filter(userViolations => userViolations.violations.length > 0);
    }

    // Filter by user
    if (selectedUser !== 'all') {
      filtered = filtered.filter(userViolations => userViolations.userInfo.id.toString() === selectedUser);
    }

    // Sort violations
    filtered.forEach(userViolations => {
      userViolations.violations.sort((a, b) => {
        if (sortBy === 'date') {
          return new Date(a.dates[0]) - new Date(b.dates[0]);
        }
        if (sortBy === 'severity') {
          const severityOrder = { 'error': 2, 'warning': 1 };
          return (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
        }
        return 0;
      });
    });

    if (sortBy === 'user') {
      filtered.sort((a, b) => {
        const nameA = `${a.userInfo.first_name} ${a.userInfo.last_name}`.toLowerCase();
        const nameB = `${b.userInfo.first_name} ${b.userInfo.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    return filtered;
  };

  const getAllUsers = () => {
    return allUsersViolations.map(userViolations => userViolations.userInfo);
  };

  const getTotalViolationsCount = () => {
    return allUsersViolations.reduce((total, userViolations) => total + userViolations.violations.length, 0);
  };

  const getViolationsCountByType = () => {
    const counts = { current: 0, future: 0, past: 0 };
    allUsersViolations.forEach(userViolations => {
      userViolations.violations.forEach(violation => {
        if (getCurrentViolation(violation.dates)) counts.current++;
        else if (isFutureViolation(violation.dates)) counts.future++;
        else if (isPastViolation(violation.dates)) counts.past++;
      });
    });
    return counts;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container" style={{ height: '400px' }}>
          <div className="loading-spinner"></div>
          <p>Loading all users' schedule violations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="alert alert-error">
          {error}
        </div>
      </div>
    );
  }

  const filteredViolations = getFilteredViolations();
  const allUsers = getAllUsers();
  const totalViolations = getTotalViolationsCount();
  const violationsCounts = getViolationsCountByType();

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Admin: All Schedule Rule Violations</h1>
        <p className="page-subtitle">
          View and manage scheduling rule violations for all users
        </p>
      </div>

      {/* Summary Stats */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">System-wide Violations Summary</h2>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Total Users with Violations</label>
            <p className="mb-0 text-lg font-bold" style={{ color: allUsersViolations.length > 0 ? '#dc2626' : '#059669' }}>
              {allUsersViolations.length}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Total Violations</label>
            <p className="mb-0 text-lg font-bold" style={{ color: totalViolations > 0 ? '#dc2626' : '#059669' }}>
              {totalViolations}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Current</label>
            <p className="mb-0 text-lg font-bold" style={{ color: violationsCounts.current > 0 ? '#dc2626' : '#6b7280' }}>
              {violationsCounts.current}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Future</label>
            <p className="mb-0 text-lg font-bold" style={{ color: violationsCounts.future > 0 ? '#d97706' : '#6b7280' }}>
              {violationsCounts.future}
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Past</label>
            <p className="mb-0 text-lg font-bold" style={{ color: '#6b7280' }}>
              {violationsCounts.past}
            </p>
          </div>
        </div>

        {!settings.mandatoryBreaks && (
          <div className="alert alert-info" style={{ marginTop: '1rem' }}>
            <strong>Note:</strong> Consecutive days violations are not shown because mandatory breaks are currently disabled.
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Filters</h2>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Time Period</label>
            <select 
              value={selectedFilter} 
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="form-control"
            >
              <option value="all">All Violations</option>
              <option value="current">Current Violations</option>
              <option value="future">Future Violations</option>
              <option value="past">Past Violations</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">User</label>
            <select 
              value={selectedUser} 
              onChange={(e) => setSelectedUser(e.target.value)}
              className="form-control"
            >
              <option value="all">All Users</option>
              {allUsers.map(user => (
                <option key={user.id} value={user.id.toString()}>
                  {user.first_name} {user.last_name} ({user.rank})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Sort By</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="form-control"
            >
              <option value="user">User Name</option>
              <option value="date">Date</option>
              <option value="severity">Severity</option>
            </select>
          </div>
        </div>
      </div>

      {/* Violations Display */}
      {filteredViolations.length === 0 ? (
        <div className="card">
          <div className="text-center" style={{ padding: '3rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h3 style={{ color: '#059669', marginBottom: '0.5rem' }}>No Violations Found</h3>
            <p style={{ color: '#6b7280' }}>
              {selectedFilter === 'all' 
                ? 'No users have any scheduling rule violations.'
                : `No violations found for the selected filters.`}
            </p>
          </div>
        </div>
      ) : (
        filteredViolations.map((userViolations, userIndex) => (
          <div key={userViolations.userInfo.id} className="card">
            <div className="card-header" style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
              <h2 className="card-title" style={{ margin: 0, color: '#374151' }}>
                👤 {userViolations.userInfo.first_name} {userViolations.userInfo.last_name} 
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: 'normal', 
                  color: '#6b7280',
                  marginLeft: '0.5rem'
                }}>
                  ({userViolations.userInfo.rank}) - {userViolations.violations.length} {userViolations.violations.length === 1 ? 'violation' : 'violations'}
                </span>
              </h2>
            </div>
            
            <div className="violations-list">
                             {userViolations.violations.map((violation, violationIndex) => {
                 const isCurrentViolation = getCurrentViolation(violation.dates);
                 const isCurrentlyFuture = isFutureViolation(violation.dates);
                
                                 let bgColor = '#f9fafb';
                 if (isCurrentViolation) bgColor = '#fef2f2';
                 else if (isCurrentlyFuture) bgColor = '#fffbeb';
                
                return (
                  <div key={violationIndex} className="violation-item" style={{ 
                    border: `2px solid ${getViolationColor(violation.severity)}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    backgroundColor: bgColor
                  }}>
                    <div className="violation-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>
                        {getViolationIcon(violation.type)}
                      </span>
                      <h3 style={{ margin: 0, color: getViolationColor(violation.severity) }}>
                        {violation.title}
                      </h3>
                                             <span style={{ 
                         marginLeft: 'auto', 
                         fontSize: '0.875rem', 
                         fontWeight: 'bold',
                         color: isCurrentViolation ? '#dc2626' : isCurrentlyFuture ? '#d97706' : '#6b7280'
                       }}>
                         {isCurrentViolation ? 'CURRENT' : isCurrentlyFuture ? 'FUTURE' : 'PAST'}
                      </span>
                    </div>
                    
                    <p style={{ margin: '0.5rem 0', fontWeight: 'bold' }}>
                      {violation.description}
                    </p>
                    
                    <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                      <strong>Dates:</strong> {formatDateRange(violation.dates)}
                    </p>
                    
                    {violation.stickInfo && (
                      <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                        <strong>Pay Period:</strong> {violation.stickInfo.payPeriod}, 
                        <strong> Stick:</strong> {violation.stickInfo.stick}
                      </p>
                    )}
                    
                    {violation.sequenceInfo && (
                      <p style={{ margin: '0.5rem 0', color: '#6b7280' }}>
                        <strong>Sequence:</strong> {violation.sequenceInfo.totalDays} consecutive days
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Rule Explanations */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Scheduling Rules</h2>
        </div>
        
        <div style={{ padding: '1rem 0' }}>
          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>
              📅 Stick Days Rule
            </h4>
            <p style={{ margin: 0, color: '#6b7280' }}>
              Maximum {STICK_MAX_AVAILABLE_DAYS} available days per stick (7-day period). Each pay period has 2 sticks.
            </p>
          </div>
          
          {settings.mandatoryBreaks && (
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>
                🔗 Consecutive Days Rule
              </h4>
              <p style={{ margin: 0, color: '#6b7280' }}>
                Maximum 5 consecutive available days. Mandatory break required after 5 days in a row.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminScheduleViolations; 